import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Shield } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AppShell, PageTitle } from "@/components/AppShell";
import { ScoreBadge } from "@/components/Chips";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";

const Card = ({ p, checked, onCheck, result }) => {
  const v = p.validation;
  const blocked = p.listing.shielded || !v.title_ok || !v.tags_ok;
  return (
    <li data-testid={`batch-proposal-${p.id}`} className="rounded-2xl border glass p-5">
      <div className="flex items-start gap-3">
        <Checkbox checked={checked} disabled={blocked || !!result?.ok} onCheckedChange={onCheck} data-testid={`batch-proposal-select-${p.id}`} className="mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ScoreBadge score={result?.score ?? p.listing.score} testid={`batch-proposal-score-${p.id}`} />
            <Link to={`/app/listings/${p.listing_id}`} className="text-sm text-muted-foreground hover:text-primary" data-testid={`batch-proposal-open-${p.id}`}>Review in detail →</Link>
            {p.listing.shielded && <span className="inline-flex items-center gap-1 text-xs text-[#E9C98B]"><Shield className="h-3 w-3" />Protected bestseller — unlock on the listing page</span>}
          </div>
          <p className="mt-2 text-sm text-muted-foreground line-through decoration-foreground/20 truncate">{p.original.title}</p>
          <p className="text-sm font-medium break-words">{p.proposed.title}</p>
          {p.reasons.title && <p className="mt-1 text-xs text-muted-foreground">{p.reasons.title}</p>}
          <p className="mt-2 font-mono text-xs text-muted-foreground">Tags {p.original.tags.length} → {p.proposed.tags.length} · Changed: {p.changed_fields.join(", ")}</p>
          {(!v.title_ok || !v.tags_ok) && <p className="mt-1 text-xs text-destructive">{!v.title_ok ? "Title doesn't meet Etsy rules." : "Tags don't meet Etsy rules."}</p>}
          {result && (
            <p data-testid={`batch-proposal-result-${p.id}`} className={`mt-2 inline-flex items-center gap-1 text-sm ${result.ok ? "text-emerald-300" : "text-destructive"}`}>
              {result.ok ? <><CheckCircle2 className="h-4 w-4" />Updated</> : <><AlertTriangle className="h-4 w-4" />{result.message}</>}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};

export default function BatchReview() {
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const [job, setJob] = useState(null);
  const [props, setProps] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);

  const loadProps = useCallback(async (ids) => {
    const { data } = await api.get("/proposals", { params: ids ? { ids: ids.join(",") } : {} });
    setProps(data.proposals);
    setSel(new Set(data.proposals.filter((p) => !p.listing.shielded && p.validation.title_ok && p.validation.tags_ok).map((p) => p.id)));
  }, []);

  useEffect(() => {
    if (!jobId) { loadProps().catch((e) => toast.error(errMsg(e))); return; }
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob(data);
        if (data.status === "done") { if (data.failed) toast.error(`${data.failed} fix(es) couldn't be generated — try again.`); await loadProps(data.proposal_ids); return; }
      } catch (e) { toast.error(errMsg(e)); return; }
      if (!stop) setTimeout(tick, 2000);
    };
    tick();
    return () => { stop = true; };
  }, [jobId, loadProps]);

  const pushAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/proposals/batch-push", { ids: [...sel], approved: true });
      setResults(Object.fromEntries(data.results.map((r) => [r.proposal_id, r])));
      setSel(new Set());
      toast.success(`${data.pushed} listing(s) updated. Backups saved.`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const running = job && job.status !== "done";
  return (
    <AppShell>
      <PageTitle eyebrow="Approval queue" title="Review fixes" testid="batch-review-title">
        {props?.length > 0 && (
          <Button className="btn-aurora rounded-full" disabled={!sel.size || busy} onClick={pushAll} data-testid="batch-approve-push-button">
            {busy ? "Pushing…" : `Approve & push (${sel.size})`}
          </Button>
        )}
      </PageTitle>
      {running && (
        <div data-testid="batch-job-progress" className="rounded-2xl border glass p-6">
          <p className="text-sm">Generating fixes… <span className="font-mono">{job.done + job.failed}/{job.total}</span></p>
          <Progress value={((job.done + job.failed) / job.total) * 100} className="mt-3" />
        </div>
      )}
      {props && !props.length && !running && <p data-testid="batch-empty" className="text-muted-foreground">No fixes waiting. Select listings in the catalog and click "Generate fixes".</p>}
      {props?.length > 0 && (
        <>
          <p className="mb-4 text-sm text-muted-foreground" data-testid="batch-proposal-count">{props.length} proposals. Only the ones you tick are pushed.</p>
          <ul className="space-y-3">
            {props.map((p) => (
              <Card key={p.id} p={p} result={results[p.id]} checked={sel.has(p.id)}
                onCheck={(v) => { const n = new Set(sel); v ? n.add(p.id) : n.delete(p.id); setSel(n); }} />
            ))}
          </ul>
        </>
      )}
    </AppShell>
  );
}
